import { useState, useCallback } from "react";
import { UserEntity } from "../../domain/entities/UserEntity";
import { UserRepositoryImpl } from "../../data/repositories/UserRepositoryImpl";
import {
  CreateUserWithPermissionsUseCase,
  UpdateUserPermissionsUseCase,
  GetAssignableManagersUseCase,
} from "../../domain/use-cases/users";

// Create repository instance (singleton)
const userRepository = new UserRepositoryImpl();

// Create use cases
const createUserWithPermissionsUseCase = new CreateUserWithPermissionsUseCase(
  userRepository
);
const updateUserPermissionsUseCase = new UpdateUserPermissionsUseCase(
  userRepository
);
const getAssignableManagersUseCase = new GetAssignableManagersUseCase(
  userRepository
);

export interface UseUserManagementReturn {
  users: UserEntity[];
  assignableManagers: Array<{
    id: number;
    email: string;
    userName?: string;
    role: string;
  }>;
  loading: boolean;
  error: Error | null;
  fetchUsers: () => Promise<void>;
  fetchAssignableManagers: () => Promise<void>;
  createUser: (data: {
    email: string;
    userName?: string;
    password: string;
    role_id: number;
    manager_id?: number;
  }) => Promise<UserEntity>;
  updateUserPermissions: (
    userId: number,
    permissions: Record<string, Record<string, boolean>>
  ) => Promise<void>;
  deleteUser: (userId: number) => Promise<void>;
}

export const useUserManagement = (): UseUserManagementReturn => {
  const [users, setUsers] = useState<UserEntity[]>([]);
  const [assignableManagers, setAssignableManagers] = useState<
    Array<{ id: number; email: string; userName?: string; role: string }>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const usersList = await userRepository.getUsers();
      setUsers(usersList);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch users"));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignableManagers = useCallback(async () => {
    try {
      setError(null);
      const managers = await getAssignableManagersUseCase.execute();
      setAssignableManagers(managers);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch assignable managers")
      );
      throw err;
    }
  }, []);

  const createUser = useCallback(
    async (data: {
      email: string;
      userName?: string;
      password: string;
      role_id: number;
      manager_id?: number;
    }): Promise<UserEntity> => {
      try {
        setError(null);
        const newUser = await createUserWithPermissionsUseCase.execute(data);
        setUsers((prev) => [...prev, newUser]);
        return newUser;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to create user")
        );
        throw err;
      }
    },
    []
  );

  const updateUserPermissions = useCallback(
    async (
      userId: number,
      permissions: Record<string, Record<string, boolean>>
    ): Promise<void> => {
      try {
        setError(null);
        await updateUserPermissionsUseCase.execute(userId, permissions);
        // Refresh users list to get updated permissions
        await fetchUsers();
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to update user permissions")
        );
        throw err;
      }
    },
    [fetchUsers]
  );

  const deleteUser = useCallback(
    async (userId: number): Promise<void> => {
      try {
        setError(null);
        await userRepository.deleteUser(userId);
        // Refresh users list to remove deleted user
        await fetchUsers();
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to delete user")
        );
        throw err;
      }
    },
    [fetchUsers]
  );

  return {
    users,
    assignableManagers,
    loading,
    error,
    fetchUsers,
    fetchAssignableManagers,
    createUser,
    updateUserPermissions,
    deleteUser,
  };
};
